import React, { useState, useEffect, useRef } from "react";
import { IonPage, IonContent, IonButton, IonText, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const SkipPattern: React.FC = () => {
    const [sequence, setSequence] = useState<number[]>([]);
    const [missingNumber, setMissingNumber] = useState<number>(0);
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Skip Counting Worksheet for LKG | Early Math Skills | Mama’s Guidance";
    }, []);

    const generateNewQuestion = () => {
        const start = Math.floor(Math.random() * 89) + 11; // Start from a random number between 1-10
        //const step = Math.floor(Math.random() * 3) + 1; // Step of 1, 2, or 3
        const step = Math.random() < 0.5 ? 2 : 5;
        const numbers = Array.from({ length: 5 }, (_, i) => start + i * step);
        const missingIndex = Math.floor(Math.random() * numbers.length);
        const missingValue = numbers[missingIndex];
        numbers[missingIndex] = "?" as any; // Placeholder for missing number

        setSequence(numbers);
        setMissingNumber(missingValue);

        // Generate options
        const wrongOption1 = missingValue + (Math.random() > 0.5 ? 1 : -1);
        const wrongOption2 = missingValue + (Math.random() > 0.5 ? 2 : -2);
        const optionsArray = [missingValue, wrongOption1, wrongOption2].sort(() => Math.random() - 0.5);
        setOptions(optionsArray);
        setMessage("");
    };

    const checkAnswer = (selected: number) => {
        if (selected === missingNumber) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
            }, 5000)
        } else {
            setMessage("❌ Oops! Try again.");
        }
        setTimeout(generateNewQuestion, 1500);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce skip counting to LKG students with fun and interactive activities on Mama’s Guidance—designed to build early math fluency and number pattern recognition through playful and engaging exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Skip Counting</h1>

                                    <p>
                                        Skip counting is a fun and essential math skill that helps children understand number patterns, grouping, and sets. This <strong>LKG skip counting worksheet</strong> introduces kids to counting in 2s, 5s, and 10s in an engaging and visual format.
                                    </p>

                                    <h2>🔢 What Is Skip Counting?</h2>
                                    <p>
                                        Skip counting means counting forward by a number other than 1. <strong>Instead of 1, 2, 3… we count like 2, 4, 6… or 5, 10, 15…</strong> It builds a foundation for multiplication and helps children recognize number sequences.
                                    </p>

                                    <p><strong>Examples:</strong></p>
                                    <ul>
                                        <li>Skip counting by 2s: 2, 4, 6, 8, 10…</li>
                                        <li>Skip counting by 5s: 5, 10, 15, 20…</li>
                                        <li>Skip counting by 10s: 10, 20, 30, 40…</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize number patterns in <strong>2s, 5s, and 10s</strong></li>
                                        <li>Develop early multiplication concepts</li>
                                        <li>Improve mental math and memory</li>
                                        <li>Enhance number sequencing and logic</li>
                                    </ul>

                                    <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Colorful number patterns with missing values</li>
                                        <li>Trace and fill the missing skip numbers</li>
                                        <li>Use of objects (<strong>e.g., apples, stars, toys</strong>) for visual learning</li>
                                        <li>Skip counting ladders and number lines</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Teach Skip Counting at Home</h2>
                                    <ul>
                                        <li>Use clapping or jumping to count in <strong>2s or 5s</strong> rhythmically</li>
                                        <li>Group everyday items (<strong>like pencils or spoons</strong>) in sets and count them</li>
                                        <li>Draw colorful charts showing skip counting numbers</li>
                                        <li>Use number songs or rhymes to help with memory</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/greater-less-than"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Greater Than / Less Than</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Let your child group toys or crayons in <strong>2s or 5s</strong> and count them. Make it a playful challenge: <strong>“How many 5s make 20?”</strong>
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Skip Counting</h1>

                                <p>
                                    Skip counting is a fun and essential math skill that helps children understand number patterns, grouping, and sets. This <strong>LKG skip counting worksheet</strong> introduces kids to counting in 2s, 5s, and 10s in an engaging and visual format.
                                </p>

                                <h2>🔢 What Is Skip Counting?</h2>
                                <p>
                                    Skip counting means counting forward by a number other than 1. <strong>Instead of 1, 2, 3… we count like 2, 4, 6… or 5, 10, 15…</strong> It builds a foundation for multiplication and helps children recognize number sequences.
                                </p>

                                <p><strong>Examples:</strong></p>
                                <ul>
                                    <li>Skip counting by 2s: 2, 4, 6, 8, 10…</li>
                                    <li>Skip counting by 5s: 5, 10, 15, 20…</li>
                                    <li>Skip counting by 10s: 10, 20, 30, 40…</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize number patterns in <strong>2s, 5s, and 10s</strong></li>
                                    <li>Develop early multiplication concepts</li>
                                    <li>Improve mental math and memory</li>
                                    <li>Enhance number sequencing and logic</li>
                                </ul>

                                <h2>📋 What&apos;s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Colorful number patterns with missing values</li>
                                    <li>Trace and fill the missing skip numbers</li>
                                    <li>Use of objects (<strong>e.g., apples, stars, toys</strong>) for visual learning</li>
                                    <li>Skip counting ladders and number lines</li>
                                </ul>

                                <h2>👩‍🏫 How to Teach Skip Counting at Home</h2>
                                <ul>
                                    <li>Use clapping or jumping to count in <strong>2s or 5s</strong> rhythmically</li>
                                    <li>Group everyday items (<strong>like pencils or spoons</strong>) in sets and count them</li>
                                    <li>Draw colorful charts showing skip counting numbers</li>
                                    <li>Use number songs or rhymes to help with memory</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/greater-less-than"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Greater Than / Less Than</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Let your child group toys or crayons in <strong>2s or 5s</strong> and count them. Make it a playful challenge: <strong>“How many 5s make 20?”</strong>
                                </p>
                            </article>
                            <IonText>
                                <h2>Find the Missing Number</h2>
                            </IonText>
                            <IonText>
                                <h1>{sequence.join(" ")}</h1>
                            </IonText>
                            <div style={{ display: 'flex', gap: '30px' }}>
                                {options.map((option, index) => (
                                    <div className="background-blue" style={{ width: 'auto', padding: '20px', fontSize: '30px' }} key={index} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {message && <IonText><h3>{message}</h3></IonText>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default SkipPattern;
